/* ============================================================================
 * api.js — EpiBreath standalone data layer (replaces Firebase Auth + API Gateway)
 * ----------------------------------------------------------------------------
 * Talks to the local Flask backend at VUE_APP_API_URL (default /api, same-origin
 * behind Nginx). Auth is a local HS256 JWT issued by POST /api/auth/login and
 * stored in localStorage — no Firebase, no Google calls.
 * ========================================================================== */
import axios from 'axios'

const API_URL = process.env.VUE_APP_API_URL || '/api'
const STORAGE_URL = process.env.VUE_APP_STORAGE_URL || '/storage'
const SESSION_KEY = 'epibreath.session' // { token, user }

/* ----------------------------- session store ---------------------------- */
const session = {
  get() {
    try {
      return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null')
    } catch (e) {
      return null
    }
  },
  set(s) {
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s))
    else localStorage.removeItem(SESSION_KEY)
    current = s
  },
}
let current = session.get()

/* ------------------------------- axios core ----------------------------- */
const http = axios.create({ baseURL: API_URL })

// Attach the bearer token to every request.
http.interceptors.request.use(cfg => {
  if (current && current.token) {
    cfg.headers = cfg.headers || {}
    cfg.headers.Authorization = `Bearer ${current.token}`
  }
  return cfg
})

// A 401 means the token is gone/expired — drop the session so guards redirect.
http.interceptors.response.use(
  r => r,
  err => {
    if (err.response && err.response.status === 401) session.set(null)
    return Promise.reject(err)
  }
)

/* ================================= AUTH ================================= */
export const auth = {
  currentUser: () => (current ? current.user : null),
  isLoggedIn: () => !!(current && current.token),

  async signIn(email, password) {
    const { data } = await http.post('/auth/login', { email, password })
    session.set({ token: data.token, user: data.user })
    return current.user
  },

  async signOut() {
    session.set(null)
  },

  // Verify the stored token against the backend on app boot.
  async restore() {
    if (!current || !current.token) return null
    try {
      const { data } = await http.get('/auth/me')
      current.user = data.user
      session.set(current)
      return current.user
    } catch (e) {
      const status = e.response && e.response.status
      if (status === 401 || status === 403) session.set(null)
      return current ? current.user : null
    }
  },
}

/* =============================== RESOURCES ============================== */
export const api = {
  async listMeasurements({ page = 1, perPage = 20, search = '' } = {}) {
    const { data } = await http.get('/measurements', {
      params: { page, per_page: perPage, search: search || undefined },
    })
    return data // { data:[...], page, per_page, total }
  },

  async getMeasurement(uuid) {
    const { data } = await http.get(`/measurements/${uuid}`)
    return data
  },

  async getStats() {
    const { data } = await http.get('/measurements/stats')
    return data
  },

  async listSuitability({ page = 1, perPage = 20 } = {}) {
    const { data } = await http.get('/suitability-tests', {
      params: { page, per_page: perPage },
    })
    return data
  },

  // Patients + devices available for scoring (form dropdowns).
  async scoringOptions() {
    const { data } = await http.get('/scoring/options')
    return data
  },

  // Upload a pos/neg RAW pair + CO2 files; the original MATLAB worker scores it.
  // Returns { uuid, report_status, output, total_vpa, free_vpa, message }.
  async scoreMeasurement({ patientUuid, newPatient, deviceUuid, title, positive_raw, negative_raw, positive_co2, negative_co2 }) {
    const fd = new FormData()
    fd.append('patient_uuid', patientUuid)
    if (newPatient) {
      fd.append('new_external_id', newPatient.external_id || '')
      fd.append('new_first_name', newPatient.first_name || '')
      fd.append('new_last_name', newPatient.last_name || '')
    }
    fd.append('device_uuid', deviceUuid)
    fd.append('title', title || '')
    fd.append('positive_raw', positive_raw)
    fd.append('negative_raw', negative_raw)
    fd.append('positive_co2', positive_co2)
    fd.append('negative_co2', negative_co2)
    const { data } = await http.post('/measurements/score', fd, { timeout: 900000 })
    return data
  },
}

// Build a URL for a stored file (RAW/txt/fig/png) served by Nginx from data/storage.
export function storageUrl(path) {
  if (!path) return null
  const clean = String(path).replace(/^\/+/, '')
  return `${STORAGE_URL}/${clean}`
}

export default { auth, api, storageUrl }
